import type paper from "paper";
import type { Project } from "../model/Project";
import { pieceDisplayOffset } from "../model/pieceTransform";
import type { GuideRay } from "../canvas/Scene";

/** Below this the ray is degenerate noise (placing right on an edge / on top of
 *  another hole) — skip it rather than draw a sub-millimetre stub. */
const MIN_GUIDE_MM = 0.5;

/** Two edge feet whose tangents differ by at least this give a genuine second
 *  axis (an X *and* a Y), which is the point of showing two edges — a coordinate
 *  the maker can track, instead of two near-parallel edges. sin(20°) ≈ 0.34. */
const DISTINCT_AXIS_SIN = 0.34;

export interface PlacementGuideOpts {
  /** An existing hole being dragged — excluded from the spacing search so it
   *  isn't measured against itself. */
  excludeHoleId?: string | null;
}

/**
 * Guide rays for a hole / hardware placement at DISPLAYED-space `pt` on part
 * `pieceId` (#728, #918/#919):
 *  - perpendicular rays to the two nearest part-outline edges that give distinct
 *    axes (the seam-allowance distances the maker is eyeballing today) — near a
 *    corner that reads as an X + Y coordinate, and it's steadier than a single
 *    nearest-point ray that hops between edges as the cursor moves, and
 *  - a ray to the nearest neighbouring hole / seated hardware (so a row lands at
 *    even spacing).
 *
 * All points are returned in DISPLAYED space (same frame as `pt`), ready to hand
 * straight to `Scene.setGuideRays`. Returns `[]` when the part is gone or every
 * candidate ray is degenerate.
 */
export function placementGuideRays(
  project: Project,
  pt: { x: number; y: number },
  pieceId: string,
  scope: paper.PaperScope,
  partLayoutView: "spread" | "assembled",
  opts?: PlacementGuideOpts,
): GuideRay[] {
  const piece = project.pieces.find((p) => p.id === pieceId);
  if (!piece) return [];
  const offset = pieceDisplayOffset(piece, partLayoutView);
  const rays: GuideRay[] = [];

  // 1. Two nearest outline edges (distinct axes) — perpendicular feet of `pt`
  //    on the part boundary (cutout edges count too; they're subpaths of the
  //    same CompoundPath).
  for (const e of nearestEdgeFeet(scope, piece.shapePathData, offset, pt)) {
    rays.push({
      from: { x: pt.x, y: pt.y },
      to: { x: e.x, y: e.y },
      lengthMm: e.d,
    });
  }

  // 2. Nearest neighbouring hole — even-spacing guide. Holes are stored in the
  //    part's ORIGINAL space; add the display offset to compare against `pt`.
  let best: { x: number; y: number; d: number } | null = null;
  for (const h of piece.holes) {
    if (opts?.excludeHoleId && h.id === opts.excludeHoleId) continue;
    const hx = h.position.x + offset.dx;
    const hy = h.position.y + offset.dy;
    const d = Math.hypot(hx - pt.x, hy - pt.y);
    if (d > MIN_GUIDE_MM && (!best || d < best.d)) best = { x: hx, y: hy, d };
  }
  if (best) {
    rays.push({
      from: { x: pt.x, y: pt.y },
      to: { x: best.x, y: best.y },
      lengthMm: best.d,
    });
  }

  return rays;
}

interface EdgeFoot {
  x: number;
  y: number;
  d: number;
  /** Unit tangent of the boundary at the foot, for the distinct-axis pick. */
  tx: number;
  ty: number;
}

/** The one or two nearest outline-edge feet for `pt`, in DISPLAYED space. The
 *  first is the closest edge overall; the second (when present) is the closest
 *  edge whose tangent gives a distinct axis, so together they read as an X + Y
 *  coordinate near a corner. Only the nearest is returned when no other edge
 *  offers a distinct axis (a degenerate near-1-D outline). Degenerate (`pt` on
 *  the boundary) feet are dropped. */
function nearestEdgeFeet(
  scope: paper.PaperScope,
  shapePathData: string,
  offset: { dx: number; dy: number },
  pt: { x: number; y: number },
): EdgeFoot[] {
  let path: paper.CompoundPath | null = null;
  try {
    path = new scope.CompoundPath({ pathData: shapePathData, insert: false });
  } catch {
    return [];
  }
  if (offset.dx !== 0 || offset.dy !== 0) {
    path.translate(new scope.Point(offset.dx, offset.dy));
  }
  const P = new scope.Point(pt.x, pt.y);
  // Every boundary curve across all subpaths (outline + cutout holes).
  const curves = path.children.length
    ? path.children.flatMap((c) => (c as paper.Path).curves ?? [])
    : (path.curves ?? []);
  const feet: EdgeFoot[] = [];
  for (const curve of curves) {
    const loc = curve.getNearestLocation(P);
    if (!loc || !loc.point) continue;
    const d = loc.point.getDistance(P);
    if (d <= MIN_GUIDE_MM) continue;
    const tan = loc.tangent;
    const tl = tan ? Math.hypot(tan.x, tan.y) : 0;
    feet.push({
      x: loc.point.x,
      y: loc.point.y,
      d,
      tx: tl > 1e-9 ? tan.x / tl : 0,
      ty: tl > 1e-9 ? tan.y / tl : 0,
    });
  }
  path.remove();
  if (!feet.length) return [];
  feet.sort((a, b) => a.d - b.d);
  const first = feet[0]!;
  // The nearest edge that gives a genuinely different axis (|cross product| of
  // the two unit tangents). Parallel edges (a strip's two sides) share an axis
  // and are skipped, so the pair always spans two directions when one exists.
  const second = feet
    .slice(1)
    .find(
      (f) => Math.abs(first.tx * f.ty - first.ty * f.tx) >= DISTINCT_AXIS_SIN,
    );
  return second ? [first, second] : [first];
}
